import { RefObject } from 'react';
import styled from 'styled-components';
import useMarkPriceWsHandler from '@paradigm/features/src/unified-rfqs/ui/rfq-websocket/useMarkPriceWsHandler';
import { usePinnedRfqsView } from '@paradigm/features/src/unified-rfqs/ui/rfqs/pinned-rfqs-context';
import { Rfq, RfqId } from '@paradigm/features/src/unified-rfqs/entities/rfq';
import {
  blackTangaroa,
  paradigmLightBlue,
} from '@paradigm/design-system/src/colors';

import PinIconFilled from '@paradigm/design-system/src/assets/PinIconFilled';

import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import RfqRow from '#/components/UnifiedRfqs/rfqs/RfqRow';
import { RfqExpansionProvider } from '#/components/UnifiedRfqs/rfqs/rfq-expansion-context';
import { useUnifiedRfqsService } from '@paradigm/features/src/unified-rfqs/contexts/unified-rfqs';

interface RfqListTableProps {
  readonly rfqIds: readonly RfqId[];
  readonly scrollableRef: RefObject<HTMLDivElement>;
}

export default function RfqListTable({
  rfqIds,
  scrollableRef,
}: RfqListTableProps) {
  // Open connection when table mounts
  useMarkPriceWsHandler();
  const testIdNotPinnedRfq = 'not-pinned-rfqs';
  const { splitPinnedRfqs } = usePinnedRfqsView();
  const { selectById } = useUnifiedRfqsService();
  const [pinnedRfqs, unpinnedRfqs] = splitPinnedRfqs(
    rfqIds.map((id) => selectById(id) || null),
  );

  if (pinnedRfqs.length === 0) {
    return (
      <RfqRows
        data-testid={testIdNotPinnedRfq}
        rfqs={unpinnedRfqs}
        scrollableRef={scrollableRef}
      />
    );
  }

  return (
    <>
      <SectionTitle>
        <PinIconContainer>
          <PinIconFilled width="10px" height="12px" />
        </PinIconContainer>
        Pinned
      </SectionTitle>
      <RfqRows
        data-testid="pinned-rfqs"
        rfqs={pinnedRfqs}
        scrollableRef={scrollableRef}
      />
      <SectionTitle css="box-shadow: inset 0 1px 0 0 rgba(41, 84, 147, 0.5);">
        All
      </SectionTitle>
      <RfqRows
        data-testid={testIdNotPinnedRfq}
        rfqs={unpinnedRfqs}
        scrollableRef={scrollableRef}
      />
    </>
  );
}

interface RfqRowsProps {
  readonly rfqs: readonly Rfq[];
  readonly scrollableRef: RefObject<HTMLDivElement>;
  readonly 'data-testid'?: string;
}

function RfqRows({
  rfqs,
  scrollableRef,
  'data-testid': dataTestid,
}: RfqRowsProps) {
  return (
    <TableContentContainer data-testid={dataTestid}>
      <RfqExpansionProvider>
        {rfqs.map((rfq) => (
          <RfqRow key={rfq.id} rfqId={rfq.id} scrollableRef={scrollableRef} />
        ))}
      </RfqExpansionProvider>
    </TableContentContainer>
  );
}

const TableContentContainer = styled.div`
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 12px 0px;
    grid-gap: 12px;
    display: grid;
    background: #020c17;
  }
`;

const SectionTitle = styled.div`
  position: relative;
  padding-left: 24px;
  height: 24px;
  font-size: 13px;
  line-height: 1;
  display: flex;
  align-items: center;
  color: ${paradigmLightBlue};
  background-color: ${blackTangaroa};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    align-items: end;
    font-size: 16px;
    height: 30px;
    justify-content: center;
    padding: 0 16px;
  }
`;

const PinIconContainer = styled.div`
  position: absolute;
  left: 7px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    position: relative;
    left: auto;
    padding-right: 4px;

    svg {
      width: 12px;
    }
  }
`;
